import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { CButton, CDialog } from '@appcues/component-library';

import { selectUser } from 'reducers/user';
import { selectAccountUsers } from 'reducers/account/users';

import CoolTip from 'components/Common/CoolTip';
import InviteModal from 'components/Team/InviteManager/InviteModal';
import { asPromised } from 'utils/as-promised';
import { inviteUser } from 'actions/account/users';
import { selectAccountFeature } from 'reducers/account/features';
import { EDITOR_PERMISSIONS_ROLE } from 'constants/features';
import { canManageAccountUsers } from 'helpers/account-management';

const StyledCDialog = styled(CDialog)`
  & > div {
    overflow-y: auto;
  }
`;

export const InviteManager = ({
  onInvite,
  disabled = false,
  enableEditorRole = false,
}) => {
  const [currentModal, setCurrentModal] = useState(null);
  const [invitedUser, setInvitedUser] = useState(null);

  const clearCurrentModal = useCallback(
    () => setCurrentModal(null),
    [setCurrentModal]
  );

  const handleInviteUser = useCallback(
    fields => {
      setInvitedUser(fields);
      return onInvite(fields)
        .then(() => setCurrentModal('INVITE_SENT'))
        .catch(error => {
          switch (error.name) {
            case 'UserInAccountError':
              setCurrentModal('USER_IN_ACCOUNT');
              break;
            case 'MaxUsersError':
              setCurrentModal('MAX_USERS_REACHED');
              break;
            default:
              setCurrentModal('ERROR');
          }
        });
    },
    [onInvite]
  );

  return (
    <>
      {currentModal === 'INVITE' && (
        <InviteModal
          enableEditorRole={enableEditorRole}
          onSubmit={handleInviteUser}
          onClose={clearCurrentModal}
        />
      )}
      {currentModal === 'USER_IN_ACCOUNT' && (
        <StyledCDialog
          title={`${invitedUser.name} is already in this account`}
          onPrimaryAction={clearCurrentModal}
        >
          Whoops! We found a user with that email in your account already! Let
          us know if they are having trouble logging in.
        </StyledCDialog>
      )}
      {currentModal === 'MAX_USERS_REACHED' && (
        <StyledCDialog
          title="Account Limit Reached"
          onPrimaryAction={clearCurrentModal}
        >
          You have reached the maximum number of users supported by the Free
          Trial. To add more users, please email{' '}
          <a href="mailto:sales@appcues.com">sales@appcues.com</a> to upgrade.
        </StyledCDialog>
      )}
      {currentModal === 'INVITE_SENT' && (
        <StyledCDialog
          title={`${invitedUser.name} has been invited to join your account`}
          onPrimaryAction={clearCurrentModal}
        >
          They will receive an e-mail to this effect with login instructions.
        </StyledCDialog>
      )}
      {currentModal === 'ERROR' && (
        <StyledCDialog
          title="We encountered an error"
          onPrimaryAction={clearCurrentModal}
        >
          Sorry about that. Please try again, and if the problem persists,
          don&apos;t hesitate to contact us.
        </StyledCDialog>
      )}
      <CoolTip
        tip="Only account admins can invite new users"
        disabled={!disabled}
      >
        <CButton
          type="positive"
          disabled={disabled}
          onClick={() => setCurrentModal('INVITE')}
        >
          Invite teammate
        </CButton>
      </CoolTip>
    </>
  );
};

InviteManager.propTypes = {
  onInvite: PropTypes.func,
  disabled: PropTypes.bool,
  enableEditorRole: PropTypes.bool,
};

const mapStateToProps = state => {
  const user = selectUser(state);
  const accountUsers = selectAccountUsers(state);
  return {
    enableEditorRole: selectAccountFeature(state, EDITOR_PERMISSIONS_ROLE),
    disabled: !canManageAccountUsers({ user, accountUsers }),
  };
};

const mapDispatchToProps = dispatch => ({
  onInvite: fields => asPromised(dispatch, inviteUser(fields)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InviteManager);
